import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  TextField,
  TextFieldProps,
  CustomTextFieldProps,
  Typography,
} from '@/primitives';
import { debounce } from 'lodash';
import { FILTER_INPUT_TYPE } from '../constants';

interface Props {
  selectProps?: SelectProps & { options: { label: string; value: string }[] };
  textFieldProps: CustomTextFieldProps;
  type: TextFieldProps['type'] | typeof FILTER_INPUT_TYPE.DROPDOWN;
  value: string;
  onChange: (value: string) => void;
  fieldLabel?: string;
  /**
   * Outlined TextField with label that sits in the field until focus or value
   * (same pattern as mating predictor / CalvingYearChipInput).
   */
  floatLabel?: string;
  /**
   * When set, only `0-9`, `-`, and `.` are accepted (keydown + paste sanitization).
   * Use for Basic Search min/max numeric fields.
   */
  restrictToNumericChars?: boolean;
  /**
   * If the blurred value should differ (e.g. clear invalid text), updates immediately and cancels pending debounce.
   */
  normalizeOnBlur?: (raw: string) => string;
}

function sanitizeNumericCharsInput(raw: string): string {
  return [...raw].filter((ch) => /[0-9.-]/.test(ch)).join('');
}

function blockNonNumericKey(e: KeyboardEvent<Element>) {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.nativeEvent.isComposing) return;
  if (e.key.length === 1 && !/[0-9.-]/.test(e.key)) {
    e.preventDefault();
  }
}

export default function BasicSearchValue({
  selectProps,
  textFieldProps,
  type,
  value: debouncedValue,
  onChange,
  fieldLabel,
  floatLabel,
  restrictToNumericChars = false,
  normalizeOnBlur,
}: Props) {
  const [value, setValue] = useState(debouncedValue);
  const [floatLabelFocused, setFloatLabelFocused] = useState(false);
  const setDebouncedValue = useMemo(() => debounce(onChange, 300), [onChange]);

  const isMultiSelect = Boolean(selectProps?.multiple);
  const parsedMultiValue = useMemo(
    () =>
      isMultiSelect
        ? debouncedValue
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
    [debouncedValue, isMultiSelect],
  );

  function renderValue(value: unknown) {
    if (
      !value ||
      (Array.isArray(value) && value.length === 0) ||
      value === ''
    ) {
      return (
        <Typography
          variant="titlemedium"
          sx={{ color: 'inherit', opacity: 0.5 }}
        >
          Select value
        </Typography>
      );
    }
    if (isMultiSelect) {
      const selected = Array.isArray(value)
        ? value
        : String(value)
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
      const labels = selected
        .map(
          (selectedValue) =>
            selectProps?.options.find((op) => op.value === selectedValue)
              ?.label ?? selectedValue,
        )
        .filter(Boolean);
      const display =
        labels.length > 2 ? `${labels.length} selected` : labels.join(', ');
      return <Typography variant="titlemedium">{display}</Typography>;
    }
    const found = selectProps?.options.find((op) => op.value === value);
    return (
      <Typography variant="titlemedium">
        {found?.label ?? (value as string)}
      </Typography>
    );
  }

  useEffect(() => {
    setValue(debouncedValue);
  }, [debouncedValue]);

  useEffect(
    () => () => {
      setDebouncedValue.cancel();
    },
    [setDebouncedValue],
  );

  if (type === FILTER_INPUT_TYPE.DROPDOWN) {
    if (!selectProps) {
      throw new Error('selectProps is required when type is "dropdown"');
    }
    const selectElement = (
      <Select
        {...selectProps}
        defaultValue=""
        displayEmpty
        label={fieldLabel}
        value={isMultiSelect ? parsedMultiValue : debouncedValue}
        onChange={(e) => {
          const nextValue = e.target.value;
          if (!isMultiSelect) {
            onChange(nextValue as string);
            return;
          }
          const selected =
            typeof nextValue === 'string'
              ? nextValue.split(',')
              : (nextValue as string[]);
          onChange(selected.filter(Boolean).join(','));
        }}
        renderValue={renderValue}
      >
        {!isMultiSelect && <MenuItem sx={{ height: '36px' }} value="" />}
        {selectProps.options.map(({ label, value }) => (
          <MenuItem key={value} value={value}>
            {isMultiSelect && (
              <Checkbox
                size="small"
                checked={parsedMultiValue.includes(value)}
                sx={{ mr: 1 }}
              />
            )}
            <Typography variant="titlemedium">{label}</Typography>
          </MenuItem>
        ))}
      </Select>
    );

    if (fieldLabel) {
      return (
        <FormControl sx={{ flex: 1, ...selectProps?.sx }}>
          <InputLabel shrink id={`bsv-label-${fieldLabel}`}>
            {fieldLabel}
          </InputLabel>
          {selectElement}
        </FormControl>
      );
    }

    return selectElement;
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = restrictToNumericChars
      ? sanitizeNumericCharsInput(e.target.value)
      : e.target.value;
    setDebouncedValue(next);
    setValue(next);
  };

  if (floatLabel) {
    const {
      onFocus: textFieldOnFocus,
      onBlur: textFieldOnBlur,
      onKeyDown: textFieldOnKeyDown,
      InputLabelProps: textFieldInputLabelProps,
      variant: textFieldVariant,
      label: _unusedLabel,
      ...restTextFieldProps
    } = textFieldProps;
    const hasValue = String(value).trim() !== '';
    const shrink =
      hasValue ||
      floatLabelFocused ||
      Boolean(textFieldInputLabelProps?.shrink);

    return (
      <TextField
        {...restTextFieldProps}
        type={type}
        value={value}
        variant={textFieldVariant ?? 'outlined'}
        label={floatLabel}
        onChange={handleChange}
        onKeyDown={(e) => {
          textFieldOnKeyDown?.(e);
          if (!restrictToNumericChars || e.defaultPrevented) return;
          blockNonNumericKey(e);
        }}
        onFocus={(e) => {
          setFloatLabelFocused(true);
          textFieldOnFocus?.(e);
        }}
        onBlur={(e) => {
          setFloatLabelFocused(false);
          textFieldOnBlur?.(e);
          if (normalizeOnBlur) {
            const next = normalizeOnBlur(String(value));
            if (next !== String(value)) {
              setDebouncedValue.cancel();
              setValue(next);
              onChange(next);
            }
          }
        }}
        slotProps={{
          ...restTextFieldProps.slotProps,
          inputLabel: {
            ...textFieldInputLabelProps,
            shrink,
          },
        }}
      />
    );
  }

  const {
    onKeyDown: textFieldOnKeyDown,
    onBlur: textFieldOnBlur,
    ...restTextFieldProps
  } = textFieldProps;

  return (
    <TextField
      {...restTextFieldProps}
      type={type}
      value={value}
      onChange={handleChange}
      onKeyDown={(e) => {
        textFieldOnKeyDown?.(e);
        if (!restrictToNumericChars || e.defaultPrevented) return;
        blockNonNumericKey(e);
      }}
      onBlur={(e) => {
        textFieldOnBlur?.(e);
        if (normalizeOnBlur) {
          const next = normalizeOnBlur(String(value));
          if (next !== String(value)) {
            setDebouncedValue.cancel();
            setValue(next);
            onChange(next);
          }
        }
      }}
    />
  );
}
